import { toaster } from '@/components/ui/toaster';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type { ApiErrorResponse, BulkEmailSendResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export interface SendGiftBulkEmailRequest {
  giftIds: string[];
  recipientUserIds: string[];
  idempotencyKey: string;
  subject: string;
  body: string;
  attachmentIds?: string[];
}

/** Sends a frozen gift selection through the existing bulk-mail worker. */
export const useSendGiftBulkEmail = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<BulkEmailSendResponse, AxiosError<ApiErrorResponse>, SendGiftBulkEmailRequest>(
    {
      mutationFn: async (request) => {
        const { data } = await api.post<{ data: BulkEmailSendResponse }>(
          '/dashboard/gifts/bulk-email',
          request
        );
        return data.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['sentBatches'] });
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys.root });
        toaster.success({ title: t('bulkEmail.send.success') });
      },
      onError: (error) => {
        const code = error.response?.data?.code;
        const fallback = error.response?.data?.message ?? t('bulkEmail.send.error.generic');
        const translated = code ? t(`errors.${code}`, { defaultValue: '' }) : '';
        toaster.error({
          title: t('bulkEmail.send.error.title'),
          description: translated || fallback,
        });
      },
    }
  );
};
